import { useEffect, useMemo, useState, type CSSProperties } from "react";

/**
 * Capa de confeti que llueve desde arriba.
 *
 * Réplica web del `ConfettiOverlay` de iOS. Cuando `play` es true, genera
 * muchas piezas de confeti como `<span>`s posicionados de forma absoluta, cada
 * uno con una animación de caída aleatoria (CSS, no JS por frame → rendimiento).
 * Se desmontan solos tras `durationMs`.
 *
 * Es un overlay `pointer-events-none`: no estorba la interacción.
 *
 * Pensado para el modo `confettiYes`.
 */
export interface ConfettiOverlayProps {
  play: boolean;
  /** Duración total en ms antes de limpiar las piezas. */
  durationMs?: number;
}

const COLORS = [
  "rgb(var(--accent))",
  "rgb(var(--accent-strong))",
  "#ffd166",
  "#ff6b9d",
  "#06d6a0",
  "#4cc9f0",
  "#f72585",
  "#ffffff",
  "#ffe4ef"
];

type Piece = {
  left: number; // %
  delay: number; // ms
  duration: number; // ms
  size: number; // px
  color: string;
  drift: number; // px de deriva horizontal
  rotate: number; // deg inicial
  shape: "petal" | "ribbon" | "dot";
  opacity: number;
};

export default function ConfettiOverlay({
  play,
  durationMs = 7200
}: ConfettiOverlayProps) {
  // `key` reinicia el overlay cada vez que `play` pasa a true, para volver
  // a soltar confeti aunque se reactive.
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!play) return;
    setActive(true);
    const t = setTimeout(() => setActive(false), durationMs);
    return () => clearTimeout(t);
  }, [play, durationMs]);

  const pieces = useMemo<Piece[]>(() => {
    if (!active) return [];
    return Array.from({ length: 520 }, (_, index) => {
      const shape = randomShape();
      const secondWave = index > 330;
      return {
        left: Math.random() * 100,
        delay: secondWave ? 1400 + Math.random() * 2600 : Math.random() * 1450,
        duration: 2800 + Math.random() * 3200,
        size: 4 + Math.random() * 13,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        drift: (Math.random() - 0.5) * 420,
        rotate: Math.random() * 540,
        shape,
        opacity: 0.72 + Math.random() * 0.28
      };
    });
  }, [active]);

  if (!active) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
      aria-hidden
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          style={pieceStyle(p)}
          // Animación por pieza: cae + deriva + gira. Inline para no generar
          // cientos de clases Tailwind dinámicas.
        />
      ))}
    </div>
  );
}

function pieceStyle(p: Piece): CSSProperties {
  return {
    position: "absolute",
    top: -20,
    left: `${p.left}%`,
    width: p.size,
    height: p.size * 0.6,
    background: p.color,
    borderRadius: p.shape === "dot" ? "999px" : p.shape === "petal" ? "999px 999px 999px 2px" : 2,
    opacity: p.opacity,
    transform: `rotate(${p.rotate}deg)`,
    animationName: "confetti-fall",
    animationDuration: `${p.duration}ms`,
    animationDelay: `${p.delay}ms`,
    animationTimingFunction: "cubic-bezier(0.25,1,0.5,1)",
    animationFillMode: "forwards",
    // Variables consumidas por el keyframe (definido en globals.css).
    ["--drift" as string]: `${p.drift}px`
  };
}

function randomShape(): Piece["shape"] {
  const value = Math.random();
  if (value < 0.18) return "dot";
  if (value < 0.58) return "petal";
  return "ribbon";
}
